import { SagaReturnType } from 'redux-saga/effects';
import { call, delay, race, SagaGenerator } from 'typed-redux-saga';

import { RetryWithTimeoutError } from './errors';

type AnyFunction = (...args: unknown[]) => unknown;

type OnSuccessCallback<Fn extends AnyFunction> = (
    result: SagaReturnType<Fn>
) => SagaGenerator<Maybe<boolean>> | Maybe<boolean>;
type OnErrorCallback = (error: unknown) => SagaGenerator<Maybe<boolean>> | Maybe<boolean>;
type Callback<Fn extends AnyFunction> = OnSuccessCallback<Fn> | OnErrorCallback | undefined;
type PollResult<Fn extends AnyFunction> = SagaReturnType<Fn> | unknown;
export interface PollParams<Fn extends AnyFunction> {
    sagaTimeout?: number;
    pollingInterval: number;
    onSuccess?: OnSuccessCallback<Fn>;
    onError?: OnErrorCallback;
}

export function* poll<Fn extends (...args: unknown[]) => unknown>(
    params: PollParams<Fn>,
    fn: Fn,
    ...args: Parameters<Fn>
) {
    const { pollingInterval, sagaTimeout, onError, onSuccess } = params;

    while (true) {
        try {
            yield* delay(pollingInterval);
            const { result, timedOut } = yield* call(function* (): SagaGenerator<{
                result?: unknown;
                timedOut?: true;
            }> {
                if (typeof sagaTimeout === 'number') {
                    const { result, timedOut } = yield* race({
                        result: call(fn, ...args),
                        timedOut: delay(sagaTimeout),
                    });
                    return {
                        result: result,
                        timedOut: timedOut,
                    };
                }
                return {
                    result: yield* call(fn, ...args),
                };
            });

            const shouldStopPollingOnSuccess = yield* call(getShouldStopPolling, onSuccess, result);
            if (shouldStopPollingOnSuccess) {
                return result;
            }
            if (timedOut) {
                throw new RetryWithTimeoutError();
            }
        } catch (error) {
            const shouldStopPollingOnError = yield* call(getShouldStopPolling, onError, error);

            if (shouldStopPollingOnError) {
                throw error;
            }
        }
    }
}

function* getShouldStopPolling<Fn extends AnyFunction>(callback: Callback<Fn>, data: PollResult<Fn>) {
    if (data && callback) {
        return yield* call(callback, data);
    }
    return false;
}
